import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, Eye, EyeOff, Loader2 } from "lucide-react";
import { DocBlocks } from "@/components/legal/DocBlocks";
import { fromEditorProgram } from "./ProgramFormFields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Live agreement preview from unsaved editor values — same layout the parent sees at registration.
export default function LiveAgreementPreview({ p, token }) {
  const [open, setOpen] = useState(false);
  const [ag, setAg] = useState(null);
  const [busy, setBusy] = useState(false);
  const payload = JSON.stringify(fromEditorProgram(p));

  useEffect(() => {
    if (!open) return undefined;
    setBusy(true);
    const t = setTimeout(() => {
      axios.post(`${API}/admin/programs/agreement-preview`, JSON.parse(payload), { headers: { "X-Admin-Token": token } })
        .then((r) => setAg(r.data)).catch(() => setAg(null)).finally(() => setBusy(false));
    }, 350);
    return () => clearTimeout(t);
  }, [open, payload, token]);

  const problems = ag ? [...(ag.errors || []), ...(ag.missing || []).map((m) => `Missing: ${m}`)] : [];
  return (
    <div className="sm:col-span-2 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
      <button type="button" data-testid="pf-preview-toggle" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-2 text-sm font-bold text-[#60a5fa]">
        {open ? <EyeOff size={15} /> : <Eye size={15} />} {open ? "Hide" : "Preview"} Enrollment Agreement
        {busy && open && <Loader2 size={14} className="animate-spin text-white/40" />}
      </button>
      {open && ag && (
        <div data-testid="pf-preview" className="mt-4">
          {problems.length > 0 ? (
            <div data-testid="pf-preview-problems" className="rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-xs font-semibold text-red-300 space-y-1">
              <p className="flex items-center gap-1.5 font-bold"><AlertTriangle size={13} /> Not ready for registration</p>
              {problems.map((m, i) => <p key={i}>• {m}</p>)}
            </div>
          ) : (
            <p data-testid="pf-preview-ready" className="flex items-center gap-1.5 text-xs font-bold text-emerald-300"><CheckCircle2 size={13} /> All required agreement details are complete.</p>
          )}
          {(ag.advisories || []).map((a, i) => <p key={i} className="mt-2 rounded-xl bg-amber-500/10 border border-amber-500/25 px-4 py-2 text-xs font-semibold text-amber-200">{a}</p>)}
          <p className="mt-3 text-[11px] font-semibold text-white/40">Amber [SAMPLE] values are filled from each family's registration and never appear in an agreement presented for acceptance.</p>
          <div className="mt-3 max-h-96 overflow-y-auto rounded-2xl border border-white/10 bg-black/30 px-4 py-3">
            {ag.header.map((h, i) => <p key={i} className={i === 1 ? "text-base font-extrabold text-white" : "text-[11px] font-bold uppercase tracking-wider text-white/40"}>{h}</p>)}
            <DocBlocks blocks={ag.blocks} compact emptyText="—" />
          </div>
        </div>
      )}
    </div>
  );
}
